import { useTourFiltersStore } from '@/entities/filter/store/tour';
import type ru from '../../i18n/ru.json';
import { useComponentI18n } from '@/shared/composables/useComponentI18n';
import { formatTripsterNumber } from '@experience/money-utils';
import { type CurrencyCode, getCurrencySymbolSafe } from '@/shared/lib/currency';
import { formatFilterPriceMoney } from '../../lib/formatFilterPriceMoney';
import { type MaybeRefOrGetter, computed, toValue } from 'vue';

type TSchema = typeof ru;

interface UseTourFilterPriceTitleProps {
  messages: TSchema;
  currencyCode: MaybeRefOrGetter<CurrencyCode | string>;
  currencySymbol?: MaybeRefOrGetter<string | undefined>;
}

export function useTourFilterPriceTitle({ messages, currencyCode, currencySymbol }: UseTourFilterPriceTitleProps) {
  const { t } = useComponentI18n({ ru: messages });

  const filterStore = useTourFiltersStore();

  const DEFAULT_TITLE = t('price.defaultTitle');

  const resolvedCurrencyCode = computed(() => String(toValue(currencyCode)).toUpperCase());

  const currentSign = computed(() => {
    const symbol = toValue(currencySymbol);

    if (symbol) {
      return symbol;
    }

    return getCurrencySymbolSafe(resolvedCurrencyCode.value);
  });

  const headerTitle = computed(() => {
    return t('price.headerPersonTitle', { currentSign: currentSign.value });
  });

  const title = computed(() => {
    const price = filterStore.searchState.price;
    const code = resolvedCurrencyCode.value;
    const sign = currentSign.value;

    const [firstPrice, secondPrice] = price;

    if (firstPrice === null && secondPrice === null) {
      return DEFAULT_TITLE;
    }

    if (firstPrice && secondPrice === null) {
      return t('price.fromPriceTitle', {
        price: formatFilterPriceMoney(firstPrice, code, sign),
      });
    }

    if (secondPrice && firstPrice === null) {
      return t('price.toPriceTitle', {
        price: formatFilterPriceMoney(secondPrice, code, sign),
      });
    }

    if (firstPrice && secondPrice) {
      return firstPrice === secondPrice
        ? formatFilterPriceMoney(secondPrice, code, sign)
        : formatFilterPriceMoney(
            `${formatTripsterNumber(firstPrice)}–${formatTripsterNumber(secondPrice)}`,
            code,
            sign,
          );
    }

    return DEFAULT_TITLE;
  });

  const isActive = computed(() => title.value !== DEFAULT_TITLE);

  return {
    title,
    headerTitle,
    isActive,
  };
}
